/**
 * 같이 보기 항목 편집.
 *
 * ── 이 화면은 원본을 쓰지 않는다 ────────────────────────────────
 *
 * 받는 것도 돌려주는 것도 `SharedTodoItem` 뿐이다. `Entry` 를 import 하지 않으므로
 * 여기서 개인 TODO 로 역반영이 일어날 수 있는 자리 자체가 없다. 저장은
 * `applyOverrides` 를 거치고, 그 함수는 `overrides` 만 바꾼다.
 *
 * ── 고치지 않은 칸은 원본을 따라간다 ────────────────────────────
 *
 * 입력칸은 표시값(`source ⊕ overrides`)으로 채워지고, 저장할 때 **원본과 같은 값은
 * override 에서 빠진다.** 그래서 제목만 고친 항목의 날짜는 계속 원본을 따라간다.
 * 어느 칸이 지금 공유 화면 값인지 칸마다 '수정됨' 으로 적어 둔다. 견줄 원본이 없는 항목(같이 보기에만
 * 만든 것)은 모든 칸이 공유 화면 값이라 표시가 아무것도 가르지 않으므로 적지 않는다.
 */
import { useState } from 'react';
import { COLORS, STATUSES } from '../domain/constants';
import {
  applyOverrides, canRevert, ownsMirror, revertToSource, sharedView,
} from '../domain/shared';
import type { ColorId, SharedOverridableField, SharedOverrides, SharedTodoItem } from '../domain/types';
import { Icon } from './Icon';
import { PICKER } from './pickerField';
import { isComposingEnter } from './ime';

interface Props {
  item: SharedTodoItem;
  /** 고친 사람으로 남길 값. 양방향이라 누가 고쳤는지가 남아야 한다. */
  myUid: string;
  /** 나에게 감춰져 있는가. 감추기는 보드 전체가 아니라 사람별이다. */
  hiddenForMe: boolean;
  mode: 'create' | 'edit';
  onSave: (item: SharedTodoItem) => void;
  /**
   * 내가 올린 항목을 보드에서 내린다. 원본은 내 캘린더에 그대로 남는다.
   *
   * 남의 항목에는 주지 않는다 — 내릴 수 있는 것은 그 원본의 주인뿐이다.
   */
  onUnshare: (item: SharedTodoItem) => void;
  /** 남이 올린 항목을 내 화면에서만 접는다. 상대 화면과 원본은 그대로다. */
  onHide: (item: SharedTodoItem) => void;
  /** 공유 화면에서만 만든 항목을 지운다. 원본이 있는 항목에는 주지 않는다. */
  onDelete: (item: SharedTodoItem) => void;
  onClose: () => void;
}

interface Form {
  title: string;
  note: string;
  color: ColorId;
  startDate: string;
  /** 종료일(선택). 비우면 하루짜리다. */
  endDate: string;
  startTime: string;
  status: string;
  important: boolean;
  urgent: boolean;
}

export function SharedItemSheet({
  item, myUid, hiddenForMe, mode, onSave, onUnshare, onHide, onDelete, onClose,
}: Props) {
  const view = sharedView(item);
  /*
    입력칸은 표시값으로 한 번만 채운다.

    열려 있는 동안 상대의 수정이 들어와도 지금 적고 있는 칸을 덮지 않는다. 다른 항목을
    열면 부르는 쪽이 `key` 로 이 조각을 새로 만들므로(`SharedScreen`), 여기서 항목이
    바뀌었는지 살피는 effect 를 두지 않는다.
  */
  const [form, setForm] = useState<Form>(() => toForm(item));
  const [error, setError] = useState('');

  const patch = (p: Partial<Form>) => setForm((f) => ({ ...f, ...p }));

  const overridden = new Set<SharedOverridableField>(view.overridden);
  // '수정됨' 은 원본과 달라진 칸이라는 뜻이다. 원본이 없는 항목에서는 가를 것이 없다.
  const mark = (k: SharedOverridableField) =>
    (!item.localOnly && item.source && overridden.has(k) ? <span className="shi-mark">수정됨</span> : null);

  const save = () => {
    const title = form.title.trim();
    if (!title) { setError('제목을 입력해 주세요.'); return; }
    // 종료일은 선택이다. 시작일과 같으면 하루짜리와 같으므로 비워 둔다.
    const endDate = form.endDate && form.endDate !== form.startDate ? form.endDate : null;
    if (endDate && !form.startDate) { setError('종료일을 적으려면 날짜를 먼저 골라 주세요.'); return; }
    if (endDate && endDate < form.startDate) { setError('종료일은 시작일보다 앞설 수 없습니다.'); return; }
    const patchFields: SharedOverrides = {
      title,
      note: form.note,
      color: form.color,
      startDate: form.startDate,
      endDate,
      startTime: form.startTime || null,
      status: asStatus(form.status),
      important: form.important,
      urgent: form.urgent,
    };
    onSave(applyOverrides(item, patchFields, myUid));
  };

  return (
    <div className="mod-back" onClick={onClose}>
      <div
        className="mod sh-mod" role="dialog" aria-modal="true"
        aria-label={mode === 'create' ? '같이 보기 새 항목' : '같이 보기 항목'}
        onClick={(e) => e.stopPropagation()}
      >
        <header className="mod-head">
          <strong className="shi-head">
            {mode === 'create' ? '같이 보기에만 만들 항목' : '같이 보기 항목'}
          </strong>
          <button className="ico-btn sm" onClick={onClose} aria-label="닫기"><Icon.X size={14} /></button>
        </header>

        <div className="mod-body">
          {item.localOnly && mode === 'edit' && (
            <p className="mod-hint">이 항목은 같이 보기에만 있습니다. 내 캘린더에는 만들어지지 않습니다.</p>
          )}
          {!item.localOnly && (
            <p className="mod-hint">
              {ownsMirror(item, myUid)
                ? <>내 캘린더의 항목입니다. 여기서 고친 값은 <b>내 캘린더에 반영되지 않고</b>, 고치지 않은 칸은 계속 원본을 따라갑니다.</>
                : <>상대의 캘린더에서 온 항목입니다. 여기서 고친 값은 <b>상대의 원본에 반영되지 않고</b>, 고치지 않은 칸은 계속 원본을 따라갑니다.</>}
            </p>
          )}

          <input
            className="mod-title"
            placeholder="제목"
            value={form.title}
            autoFocus
            onChange={(e) => patch({ title: e.target.value })}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !isComposingEnter(e) && (e.metaKey || e.ctrlKey)) save();
            }}
          />

          {/* 달력에서 항목을 가르는 값이라 제목 바로 아래에 둔다. */}
          <div className="mod-row">
            <span className="mod-lbl">색상{mark('color')}</span>
            <div className="mod-colors">
              {COLORS.map((c) => (
                <button
                  key={c.id}
                  className={'mod-color' + (form.color === c.id ? ' on' : '')}
                  style={{ background: c.hex }}
                  onClick={() => patch({ color: c.id })}
                  aria-label={c.label}
                  aria-pressed={form.color === c.id}
                />
              ))}
            </div>
          </div>

          <div className="mod-row">
            <label className="mod-lbl" htmlFor="sh-date">날짜{mark('startDate')}</label>
            <div className="mod-dt">
              <input
                id="sh-date" type="date" className="mod-input" {...PICKER}
                value={form.startDate}
                onChange={(e) => patch({ startDate: e.target.value })}
              />
              <input
                type="time" className="mod-input time" {...PICKER} value={form.startTime}
                onChange={(e) => patch({ startTime: e.target.value })}
                aria-label="시각"
              />
            </div>
          </div>

          <div className="mod-row">
            <label className="mod-lbl" htmlFor="sh-end">종료일{mark('endDate')}</label>
            <input
              id="sh-end" type="date" className="mod-input" {...PICKER}
              value={form.endDate} min={form.startDate || undefined}
              onChange={(e) => patch({ endDate: e.target.value })}
              placeholder="선택"
            />
          </div>

          <div className="mod-row">
            <span className="mod-lbl">상태{mark('status')}</span>
            <div className="mod-chips">
              {STATUSES.map((s) => (
                <button
                  key={s.id}
                  className={'chip' + (form.status === s.id ? ' on' : '')}
                  onClick={() => patch({ status: s.id })}
                  aria-pressed={form.status === s.id}
                >
                  <span className="chip-dot" style={{ ['--c' as string]: s.dot }} />{s.label}
                </button>
              ))}
            </div>
          </div>

          <div className="mod-row">
            <span className="mod-lbl">속성{mark('important') ?? mark('urgent')}</span>
            <div className="mod-chips">
              <button
                className={'chip' + (form.important ? ' on' : '')}
                onClick={() => patch({ important: !form.important })}
                aria-pressed={form.important}
              >
                <Icon.Star size={12} />중요
              </button>
              <button
                className={'chip' + (form.urgent ? ' on' : '')}
                onClick={() => patch({ urgent: !form.urgent })}
                aria-pressed={form.urgent}
              >
                <Icon.Flame size={12} />긴급
              </button>
            </div>
          </div>

          <div className="mod-row">
            <label className="mod-lbl" htmlFor="sh-note">메모{mark('note')}</label>
            <textarea
              id="sh-note" className="mod-input note" rows={3} value={form.note}
              onChange={(e) => patch({ note: e.target.value })}
              placeholder="선택"
            />
          </div>

          {canRevert(item) && (
            <div className="mod-row">
              <span className="mod-lbl" />
              <button
                className="btn sm"
                onClick={() => onSave(revertToSource(item))}
              >
                <Icon.Undo size={13} /> 원본대로 되돌리기
              </button>
            </div>
          )}

          {error && <p className="mod-err" role="alert">{error}</p>}
        </div>

        <footer className="mod-foot">
          {/*
            내 항목과 남의 항목은 할 수 있는 일이 다르다.

            내가 올린 것은 **보드에서 내릴 수 있다** — 상대에게 보이기 싫은 항목이
            여기까지 왔다면 필요한 것은 내 화면에서 접는 것이 아니라 상대 화면에서
            없애는 것이다. 남이 올린 것은 내릴 수 없으므로(규칙이 주인만 허용한다)
            할 수 있는 일은 내 화면에서 접는 것뿐이고, 그렇게 적는다.
          */}
          {mode === 'edit' && (
            item.localOnly ? (
              <button className="btn danger ghost" onClick={() => onDelete(item)}>
                <Icon.Trash size={14} /> 삭제
              </button>
            ) : ownsMirror(item, myUid) ? (
              <button className="btn danger ghost" onClick={() => onUnshare(item)}>
                <Icon.Lock size={14} /> 공유에서 내리기
              </button>
            ) : (
              <button className="btn ghost" onClick={() => onHide(item)}>
                <Icon.EyeOff size={14} /> {hiddenForMe ? '다시 보이기' : '내 화면에서만 감추기'}
              </button>
            )
          )}
          <div className="spacer" />
          <button className="btn" onClick={onClose}>취소</button>
          <button className="btn primary" onClick={save}>저장</button>
        </footer>
      </div>
    </div>
  );
}

function toForm(item: SharedTodoItem): Form {
  const v = sharedView(item);
  return {
    title: v.title,
    note: v.note,
    color: v.color,
    startDate: v.startDate,
    endDate: v.endDate ?? '',
    startTime: v.startTime ?? '',
    status: v.status,
    important: v.important,
    urgent: v.urgent,
  };
}

function asStatus(v: string): 'planned' | 'in-progress' | 'done' {
  return v === 'in-progress' || v === 'done' ? v : 'planned';
}
